import dayjs from 'dayjs';
import { ToWords } from 'to-words';

const toWords = new ToWords({ localeCode: 'en-US' });
import { formatToCurrency } from '@/lib/helpers';

/**
 * Generate the offer letter document
 *
 * @param {Object=} data - The data to be used to generate the document
 */
export const generateOfferLetter = (data = {}) => {
  const bigMargin = 20;
  /**@type {import('pdfmake/interfaces').TDocumentDefinitions} */
  const dd = {
    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    },
    info: {
      title: 'Offer Letter', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'offer letter, loan, finance', // keywords associated with the document
      author: 'Sodality Co-operative Credit Union Limited', // company name
      subject: 'Offer Letter', // name of the document being printed
      creator: 'John Doe', // name of the logged in user
      producer: 'Sodality Co-operative Credit Union Limited' // company name
    },
    content: [
      {
        columns: [
          { text: data.data || dayjs().format('MMMM DD, YYYY') },
          { text: 'Our Ref: AS/64466663', bold: true, alignment: 'right' }
        ]
      },
      {
        marginTop: 20,
        text: 'TANIEL S. LOBBAN'
      },
      '1234 Main Street',
      'Spanish Town',
      'St. Catherine',
      {
        marginTop: 20,
        text: 'Dear Ms. Lobban;'
      },
      {
        marginTop: 20,
        marginBottom: 20,
        text: "We are pleased to make you (hereinafter the “BORROWER”) this Offer of Finance of the project as outlined in the BORROWER's application to  Sodality Co-operative Credit Union Limited (hereinafter “COK SODALITY”). The basic terms and conditions of our offer are set out below and shall apply should our offer be accepted:"
      },
      {
        separator: ['', '.\t'],
        ol: [
          {
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'AMOUNT OF LOAN FACILITY\n',
                decoration: 'underline',
                bold: true
              },
              {
                text: [
                  'The sum of ',
                  {
                    text: `${toWords
                      .convert(600000, {
                        currency: true,
                        doNotAddOnly: true
                      })
                      .toUpperCase()} (${formatToCurrency(600000)})\n`,
                    bold: true
                  }
                ]
              },
              'the breakdown is as follows:-\n',
              'Previous Loan Amount  Nil\n',
              `Current Loan Amount  ${formatToCurrency(600000)}\n`,
              'The loan shall be repaid in Jamaican Dollars.'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'PURPOSE OF LOAN\n',
                decoration: 'underline',
                bold: true
              },
              'Motor Vehicle Purchase'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'RATE AND PAYMENT OF INTEREST\n',
                decoration: 'underline',
                bold: true
              },
              {
                bold: true,
                text: `${toWords
                  .convert(7.5, {
                    doNotAddOnly: true
                  })
                  .toUpperCase()} PERCENT (${7.5}%) `
              },
              'per annum on the addon balance (variable at the discretion of Work Smarter Co) payable on or before the 30th day of each month after disbursement and until the loan obligation is satisfactorily repaid.'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'PAYMENT OF PRINCIPAL\n',
                decoration: 'underline',
                bold: true
              },
              'Principal to be paid in monthly installments, the first payment to be made on or before the 30th day of each month after disbursement and until the loan obligation is satisfactorily repaid'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'ORDINARY SHARES CONTRIBUTION\n',
                decoration: 'underline',
                bold: true
              },
              'It is hereby agreed and understood that the BORROWER will make a further payment of 1.50% of total monthly payment (principal & interest) per month to shares (referred to as Share-Contribution).'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'MONTHLY PAYMENTS AND LOAN TERM\n',
                decoration: 'underline',
                bold: true
              },
              'The sum of FORTY FOUR THOUSAND, FOUR HUNDRED AND SIX DOLLARS AND TWENTY FIVE CENTS ($44,406.25) per month for ONE HUNDRED AND TWENTY (120) months, which includes the Ordinary Share Contribution.'
            ]
          },
          {
            marginTop: 10,
            marginBottom: bigMargin / 2,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'DEFAULT INTEREST CHARGE\n',
                decoration: 'underline',
                bold: true
              }
            ]
          },
          {
            marginLeft: 20,
            ul: [
              {
                text: 'To pay to Work Smarter Co each monthly installment due and payable until the loan facility is fully liquidated. In the event the monthly payments fall short of the prescribed amount, Work Smarter Co shall have the right at any time and without notice to consolidate all or any of my accounts and set-off or transfer any sum or sums standing to the credit of any one or more of such accounts in or towards satisfaction of any of my liabilities to Work Smarter Co on any other account or in any other respect whether such liabilities be actual or contingent or primary or collateral or several or joint.',
                marginBottom: bigMargin / 2
              },
              {
                text: 'Should the monthly payment of Loan and Interest as set out in Clause 6 above fall in arrears for two or more months (consecutive or combined) to pay a fee of 2.00% per month on the total arrears beginning the second month and each month thereafter until all such arrears have been paid.',
                marginBottom: bigMargin / 2
              },
              {
                text: "On demand to pay to Work Smarter Co all costs charges and expenses incurred or to be incurred by Work Smarter Co from time to time in relation to these presents or any default thereunder or the enforcement or protection of any rights to Work Smarter Co under these presents. Attorney's fees to be on an indemnity basis.",
                marginBottom: bigMargin / 2
              }
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'DUTY TO INSURE\n',
                decoration: 'underline',
                bold: true
              },
              'To insure and keep insured the said Motor vehicle and if in default it shall be lawful for the Work Smarter Co to insure and keep insured the said Motor vehicle and be reimbursed on demand any payment made for insurance along with interest.'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'CONCLUSIVE EVIDENCE\n',
                decoration: 'underline',
                bold: true
              },
              'Any statement of an officer of Work Smarter Co as to the amount remaining unpaid to Work Smarter Co at any time by the BORROWER shall be conclusive evidence and shall, in any event, be prima facie evidence as to the amount remaining unpaid to Work Smarter Co at such time by the BORROWER.'
            ]
          },
          {
            marginTop: 10,
            marginBottom: bigMargin / 2,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'CREDIT REPORTING\n',
                decoration: 'underline',
                bold: true
              }
            ]
          },
          {
            marginLeft: 20,
            ul: [
              {
                text: 'Consent shall be given for disclosure of BORROWER’s information under the Credit Reporting Act to verify credit worthiness',
                marginBottom: bigMargin / 2
              },
              {
                text: 'A non-refundable fee of THREE THOUSAND DOLLARS AND ZERO CENTS ($3,000.00) PLUS G.C.T for obtaining a credit report.',
                marginBottom: bigMargin / 2
              }
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'DOCUMENTATION FEE\n',
                decoration: 'underline',
                bold: true
              },
              'A non-refundable fee of SIXTY THOUSAND DOLLARS AND ZERO CENTS ($60,000.00) PLUS G.C.T is payable on acceptance of the offer herein.'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'PROCESSING FEE\n',
                decoration: 'underline',
                bold: true
              },
              'A non-refundable fee of 2.00% of funds to be disbursed PLUS G.C.T. is payable on acceptance of the offer herein'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'COMMITMENT FEE\n',
                decoration: 'underline',
                bold: true
              },
              'A non-refundable fee of 1.50% of funds to be disbursed PLUS G.C.T. is payable on acceptance of the offer herein.'
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'DATA PROTECTION\n',
                decoration: 'underline',
                bold: true
              },
              'As far as is reasonably required, for the service being offered by the Work Smarter Co in regards to the execution and performance of this Agreement and its securities: BORROWER consents to the collection, use, disclosure and processing of personal data and information provided by BORROWER.'
            ]
          },
          {
            marginTop: 10,
            marginBottom: bigMargin / 2,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'SECURITY\n',
                decoration: 'underline',
                bold: true
              }
            ]
          },
          {
            marginLeft: 20,
            type: 'lower-alpha',
            ol: [
              {
                text: 'A Promissory Note from the Borrower in the amount of $3,000,000.00',
                marginBottom: bigMargin / 2
              },
              {
                text: 'Standard Cuna Mutual Loan Protection Insurance covering $800,000.00 of total loan amount held with Work Smarter Co',
                marginBottom: bigMargin / 2
              },
              {
                text: 'Comprehensive Insurance with Work Smarter Co’s Interest noted to be placed on motor vehicle',
                marginBottom: bigMargin / 2
              }
            ]
          },
          {
            marginTop: 10,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'FEE for the RELEASE OF SECURITY\n',
                decoration: 'underline',
                bold: true
              },
              'A fee of TWO THOUSAND, FIVE HUNDRED DOLLARS AND ZERO CENTS ($2,500.00) plus G.C.T (AT THE PREVAILING RATE) being the cost to prepare and effect the release of the applicable security set out above upon the satisfactory repayment of the loan payment of interest, costs, fees, charges, expenses incurred and sums owing to Work Smarter Co, is payable. ',
              {
                text: "This fee is subject to change in line with the fees prevailing at the time of the BORROWER's request for release of the security(ies).\n\n",
                bold: true
              },
              {
                text: "All costs, fees, expenses and other charges, whether legal or otherwise, inclusive of stamp duties where applicable by any relevant law and Attorney's costs in connection with this Agreement will be for the BORROWER's account, on a full indemnity basis, and will be paid to Work Smarter Co on demand.\n\n"
              },
              {
                text: 'The BORROWER undertakes to pay, on demand, the cost to Work Smarter Co for the time being (as determined by Work Smarter Co in its sole discretion) of complying with any reserve, special deposit or any other requirements of the any applicable regulatory authority as affecting the transaction hereunder.'
              }
            ]
          },
          {
            marginTop: 10,
            marginBottom: bigMargin / 2,
            preserveLeadingSpaces: true,
            text: [
              {
                text: 'CONDITIONS PRECEDENT TO DISBURSEMENT\n',
                decoration: 'underline',
                bold: true
              },
              'The obligation of Work Smarter Co to make the loan available to the member shall be subject to the following:'
            ]
          },
          {
            marginLeft: 20,
            type: 'lower-alpha',
            ol: [
              {
                text: 'Signing and returning of this Offer Letter;',
                marginBottom: bigMargin / 2
              },
              {
                text: 'Permanent shares of $20,000.00 that will remain intact with the Credit Union until all amounts due and payable hereunder has been satisfied;',
                marginBottom: bigMargin / 2
              },
              {
                text: "Execution of the LOAN AGREEMENT and any other documents as shall be required by Work Smarter Co's Legal Counsel having due regard to the collateral security requirements herein before mentioned.",
                marginBottom: bigMargin / 2
              },
              {
                text: 'Add something useful to this',
                marginBottom: bigMargin / 2
              }
            ]
          }
        ]
      },
      {
        text: 'In acceptance of our Offer of Finance, please sign the copy letter enclosed at the place indicated. Then return to us within FOURTEEN (14) days of the date hereof together with the sum of ONE HUNDRED AND FIVE THOUSAND DOLLARS AND ZERO CENTS ($105,000.00) PLUS G.C.T AT THE PREVAILING RATE payable by cash, transfer or Managers Cheque. This being the Fees referred to in Clauses 12 and 13 hereof. Failure to do so will be deemed non-acceptance of our offer.',
        bold: true,
        marginBottom: bigMargin,
        marginTop: bigMargin
      },
      {
        text: 'Notwithstanding the acceptance of the Offer herein, should the BORROWER fail to satisfy all the conditions precedent to disbursement in accordance with Clause 17 hereof within FOURTEEN (14) days of the date of acceptance hereof or should Work Smarter Co become aware of any material factor which in its sole opinion mitigates against its making the loan Work Smarter Co reserves the right at its sole discretion to cancel the facility herein.',
        bold: true,
        marginBottom: bigMargin * 2
      },
      { text: 'Yours co-operatively,', marginBottom: bigMargin / 2 },
      { text: data.company?.name, bold: true },
      {
        text: '_____________________________________________________',
        font: 'Roboto',
        marginTop: bigMargin
      },
      {
        text: 'YANIK BLACK (Mr./Ms./Mrs.), MANAGER - HEAD OFFICE',
        bold: true
      },
      { text: 'ACCEPTED BY:', marginTop: bigMargin * 2 },
      {
        text: '_____________________________________________________',
        font: 'Roboto',
        marginTop: bigMargin
      },
      {
        text: ['BORROWER - ', { text: 'MARIA R HALL', bold: true }]
      },
      {
        marginTop: bigMargin * 2,
        text: [
          'On the  ',
          { text: '_________', font: 'Roboto' },
          ' day of ',
          { text: '_________________________', font: 'Roboto' },
          ' 20 ',
          { text: '_____', font: 'Roboto' }
        ]
      },
      {
        marginTop: bigMargin * 2,
        columns: [
          {
            text: [
              { text: 'Witnessed by: ', bold: true },
              { text: '_________________________________\n', font: 'Roboto' }
            ]
          },
          {
            alignment: 'right',
            text: [
              { text: '____________________________________\n', font: 'Roboto' }
            ]
          }
        ]
      },
      {
        columns: [
          {
            text: 'Name of WITNESS',
            alignment: 'right',
            marginRight: bigMargin * 3
          },
          {
            text: '\t\tSignature of WITNESS',
            alignment: 'right',
            marginRight: bigMargin * 2
          }
        ]
      },
      {
        marginTop: bigMargin * 2,
        text: [
          'On the  ',
          { text: '_________', font: 'Roboto' },
          ' day of ',
          { text: '_________________________', font: 'Roboto' },
          ' 20 ',
          { text: '_____', font: 'Roboto' }
        ]
      },
      {
        text: '_____________________________________________________',
        font: 'Roboto',
        marginTop: bigMargin
      },
      {
        text: `Preparation Officer: ${data?.user?.username}`,
        bold: true
      }
    ],
    defaultStyle: {
      font: 'Lora',
      color: '#0f172a',
      fontSize: 11,
      lineHeight: 1.3
    }
  };

  return dd;
};
